import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Building2, Users, ShoppingCart, Plus, Pencil, LogOut, Search, BarChart3, Power,
  Monitor, Printer, CookingPot, CreditCard, ScanBarcode, Package, DollarSign,
  Trash2, ChevronRight, ArrowLeft, Layers, Receipt, X
} from "lucide-react";

const ENV_URL = process.env.REACT_APP_BACKEND_URL || "";
const envHostname = (() => { try { return new URL(ENV_URL).hostname; } catch { return ""; } })();
const API_BASE = (envHostname && envHostname === window.location.hostname) ? ENV_URL : window.location.origin;
const api = axios.create({ baseURL: `${API_BASE}/api` });

const CATEGORY_OPTIONS = [
  { value: "station", label: "POS Station", icon: Monitor },
  { value: "peripheral", label: "Peripheral", icon: Printer },
  { value: "software", label: "Software", icon: Layers },
  { value: "service", label: "Service", icon: Package },
];

const categoryIcon = (cat) => {
  const map = { station: Monitor, peripheral: Printer, software: Layers, service: Package, kds: CookingPot, payment: CreditCard, scanner: ScanBarcode };
  return map[cat] || Package;
};

const planColors = { trial: "bg-amber-100 text-amber-800", basic: "bg-blue-100 text-blue-800", pro: "bg-purple-100 text-purple-800", enterprise: "bg-emerald-100 text-emerald-800" };
const statusColors = { active: "bg-emerald-100 text-emerald-800", suspended: "bg-red-100 text-red-800", cancelled: "bg-slate-200 text-slate-600" };

export default function PlatformAdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("platform_token") || "");
  const [admin, setAdmin] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Navigation
  const [activeTab, setActiveTab] = useState("accounts"); // accounts | catalog | account-detail
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Login
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Catalog
  const [catalog, setCatalog] = useState([]);
  const [showCatalogDialog, setShowCatalogDialog] = useState(false);
  const [editingCatalogItem, setEditingCatalogItem] = useState(null);
  const [catalogForm, setCatalogForm] = useState({ name: "", category: "station", description: "", monthly_cost: 0, one_time_cost: 0, is_active: true, icon: "", sort_order: 0 });

  // Account dialog
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({ business_name: "", address: "", city: "", state: "", zip_code: "", phone: "", email: "", logo_url: "", plan: "trial", status: "active" });

  // Provisions
  const [provisions, setProvisions] = useState([]);
  const [billing, setBilling] = useState(null);
  const [showProvisionDialog, setShowProvisionDialog] = useState(false);
  const [editingProvision, setEditingProvision] = useState(null);
  const [provisionForm, setProvisionForm] = useState({ catalog_item_id: "", quantity: 1, cost_override: "", notes: "", is_active: true });

  const authApi = useCallback(() => {
    return axios.create({ baseURL: `${API_BASE}/api`, headers: { Authorization: `Bearer ${token}` } });
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const a = authApi();
      const [meRes, accRes, statsRes, catRes] = await Promise.all([
        a.get("/platform/me"), a.get("/platform/accounts"), a.get("/platform/stats"), a.get("/platform/catalog"),
      ]);
      setAdmin(meRes.data);
      setAccounts(accRes.data);
      setStats(statsRes.data);
      setCatalog(catRes.data);
    } catch {
      localStorage.removeItem("platform_token");
      setToken(""); setAdmin(null);
    }
    setLoading(false);
  }, [token, authApi]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchAccountDetail = useCallback(async (accountId) => {
    try {
      const a = authApi();
      const [provRes, billRes] = await Promise.all([
        a.get(`/platform/accounts/${accountId}/provisions`),
        a.get(`/platform/accounts/${accountId}/billing`),
      ]);
      setProvisions(provRes.data);
      setBilling(billRes.data);
    } catch { toast.error("Failed to load account details"); }
  }, [authApi]);

  useEffect(() => {
    if (selectedAccount && activeTab === "account-detail") fetchAccountDetail(selectedAccount.account_id);
  }, [selectedAccount, activeTab, fetchAccountDetail]);

  // Auth
  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      const res = await api.post("/platform/login", { email: loginEmail, password: loginPassword });
      localStorage.setItem("platform_token", res.data.session_token);
      setToken(res.data.session_token); setAdmin(res.data.admin);
      toast.success("Welcome, Platform Admin");
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    setLoginLoading(false);
  };
  const handleLogout = () => { localStorage.removeItem("platform_token"); setToken(""); setAdmin(null); setAccounts([]); setStats(null); };

  // Account CRUD
  const openCreateAccount = () => {
    setEditingAccount(null);
    setAccountForm({ business_name: "", address: "", city: "", state: "", zip_code: "", phone: "", email: "", logo_url: "", plan: "trial", status: "active" });
    setShowAccountDialog(true);
  };
  const openEditAccount = (acc) => {
    setEditingAccount(acc);
    setAccountForm({ business_name: acc.business_name || "", address: acc.address || "", city: acc.city || "", state: acc.state || "", zip_code: acc.zip_code || "", phone: acc.phone || "", email: acc.email || "", logo_url: acc.logo_url || "", plan: acc.plan || "trial", status: acc.status || "active" });
    setShowAccountDialog(true);
  };
  const saveAccount = async () => {
    if (!accountForm.business_name.trim()) { toast.error("Business name required"); return; }
    try {
      const a = authApi();
      if (editingAccount) { await a.put(`/platform/accounts/${editingAccount.account_id}`, accountForm); toast.success("Account updated"); }
      else { await a.post("/platform/accounts", accountForm); toast.success("Account created"); }
      setShowAccountDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const toggleStatus = async (acc) => {
    const newStatus = acc.status === "active" ? "suspended" : "active";
    try { await authApi().put(`/platform/accounts/${acc.account_id}/status`, { status: newStatus }); toast.success(`Account ${newStatus}`); fetchData(); }
    catch { toast.error("Failed to update status"); }
  };

  // Catalog CRUD
  const openCreateCatalog = () => {
    setEditingCatalogItem(null);
    setCatalogForm({ name: "", category: "station", description: "", monthly_cost: 0, one_time_cost: 0, is_active: true, icon: "", sort_order: 0 });
    setShowCatalogDialog(true);
  };
  const openEditCatalog = (item) => {
    setEditingCatalogItem(item);
    setCatalogForm({ name: item.name || "", category: item.category || "station", description: item.description || "", monthly_cost: item.monthly_cost || 0, one_time_cost: item.one_time_cost || 0, is_active: item.is_active !== false, icon: item.icon || "", sort_order: item.sort_order || 0 });
    setShowCatalogDialog(true);
  };
  const saveCatalogItem = async () => {
    if (!catalogForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const a = authApi();
      const payload = { ...catalogForm, monthly_cost: parseFloat(catalogForm.monthly_cost) || 0, one_time_cost: parseFloat(catalogForm.one_time_cost) || 0 };
      if (editingCatalogItem) { await a.put(`/platform/catalog/${editingCatalogItem.catalog_item_id}`, payload); toast.success("Item updated"); }
      else { await a.post("/platform/catalog", payload); toast.success("Item created"); }
      setShowCatalogDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteCatalogItem = async (itemId) => {
    if (!window.confirm("Delete this catalog item? This will also remove it from all accounts.")) return;
    try { await authApi().delete(`/platform/catalog/${itemId}`); toast.success("Deleted"); fetchData(); }
    catch { toast.error("Delete failed"); }
  };

  // Provision CRUD
  const openAddProvision = () => {
    setEditingProvision(null);
    setProvisionForm({ catalog_item_id: catalog[0]?.catalog_item_id || "", quantity: 1, cost_override: "", notes: "", is_active: true });
    setShowProvisionDialog(true);
  };
  const openEditProvision = (p) => {
    setEditingProvision(p);
    setProvisionForm({ catalog_item_id: p.catalog_item_id || "", quantity: p.quantity || 1, cost_override: p.cost_override != null ? String(p.cost_override) : "", notes: p.notes || "", is_active: p.is_active !== false });
    setShowProvisionDialog(true);
  };
  const saveProvision = async () => {
    if (!provisionForm.catalog_item_id) { toast.error("Select an item"); return; }
    try {
      const a = authApi();
      const payload = { ...provisionForm, quantity: parseInt(provisionForm.quantity) || 1, cost_override: provisionForm.cost_override !== "" ? parseFloat(provisionForm.cost_override) : null };
      if (editingProvision) { await a.put(`/platform/provisions/${editingProvision.provision_id}`, payload); toast.success("Updated"); }
      else { await a.post(`/platform/accounts/${selectedAccount.account_id}/provisions`, payload); toast.success("Provisioned"); }
      setShowProvisionDialog(false); fetchAccountDetail(selectedAccount.account_id); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteProvision = async (provId) => {
    try { await authApi().delete(`/platform/provisions/${provId}`); toast.success("Removed"); fetchAccountDetail(selectedAccount.account_id); fetchData(); }
    catch { toast.error("Delete failed"); }
  };

  const filteredAccounts = accounts.filter((a) =>
    !searchQuery || a.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) || a.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openAccountDetail = (acc) => { setSelectedAccount(acc); setActiveTab("account-detail"); };

  // Login Screen
  if (!token || !admin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="platform-login">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="h-12 w-12 bg-slate-900 rounded-xl flex items-center justify-center mx-auto mb-3"><Building2 className="h-6 w-6 text-white" /></div>
            <h1 className="text-xl font-bold text-slate-900">CashUpPOS Platform</h1>
            <p className="text-sm text-slate-500 mt-1">Super Admin Login</p>
          </div>
          <div className="space-y-3">
            <div><Label>Email</Label><Input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="platform@cashuppos.com" className="mt-1" data-testid="platform-login-email" /></div>
            <div><Label>Password</Label><Input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Password" className="mt-1" data-testid="platform-login-password" onKeyDown={(e) => { if (e.key === "Enter") handleLogin(); }} /></div>
            <Button onClick={handleLogin} disabled={loginLoading} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="platform-login-btn">{loginLoading ? "Signing in..." : "Sign In"}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="platform-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-white/10 rounded-lg flex items-center justify-center"><Building2 className="h-4 w-4" /></div>
          <div>
            <h1 className="text-sm font-bold">CashUpPOS Platform</h1>
            <p className="text-[10px] text-white/50">Super Admin Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Nav tabs */}
          <nav className="flex items-center gap-1">
            {[
              { id: "accounts", label: "Accounts", icon: Building2 },
              { id: "catalog", label: "Service Catalog", icon: Package },
            ].map((t) => (
              <button key={t.id} onClick={() => { setActiveTab(t.id); setSelectedAccount(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === t.id || (t.id === "accounts" && activeTab === "account-detail") ? "bg-white/20 text-white" : "text-white/60 hover:text-white hover:bg-white/10"}`}
                data-testid={`nav-${t.id}`}>
                <t.icon className="h-3.5 w-3.5" />{t.label}
              </button>
            ))}
          </nav>
          <div className="h-4 w-px bg-white/20" />
          <span className="text-xs text-white/70">{admin.email}</span>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="platform-logout"><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        {/* Stats */}
        {stats && activeTab !== "account-detail" && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="platform-stats">
            {[
              { label: "Total Accounts", value: stats.total_accounts, icon: Building2, color: "text-blue-600" },
              { label: "Active", value: stats.active_accounts, icon: Power, color: "text-emerald-600" },
              { label: "Trial", value: stats.trial_accounts, icon: BarChart3, color: "text-amber-600" },
              { label: "Users", value: stats.total_users, icon: Users, color: "text-purple-600" },
              { label: "Orders", value: stats.total_orders, icon: ShoppingCart, color: "text-slate-600" },
              { label: "Provisions", value: stats.total_provisions || 0, icon: Monitor, color: "text-cyan-600" },
              { label: "MRR", value: `$${(stats.total_mrr || 0).toLocaleString()}`, icon: DollarSign, color: "text-green-600" },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                <div className="flex items-center gap-1.5 mb-0.5"><s.icon className={`h-3.5 w-3.5 ${s.color}`} /><span className="text-[10px] text-slate-500">{s.label}</span></div>
                <p className="text-xl font-bold text-slate-900">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* === ACCOUNTS TAB === */}
        {activeTab === "accounts" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Accounts</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search accounts..." className="pl-8 h-8 text-xs w-48" data-testid="platform-search" />
                </div>
                <Button onClick={openCreateAccount} className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8" data-testid="platform-create-account"><Plus className="h-3.5 w-3.5 mr-1" /> New Account</Button>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="platform-accounts-table">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-600 px-4 py-2.5">Business</th>
                    <th className="text-left text-xs font-semibold text-slate-600 px-4 py-2.5">Contact</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-4 py-2.5">Plan</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-4 py-2.5">Status</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-4 py-2.5">Users</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-4 py-2.5">Created</th>
                    <th className="text-right text-xs font-semibold text-slate-600 px-4 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">No accounts yet</td></tr>
                  ) : filteredAccounts.map((acc) => (
                    <tr key={acc.account_id} className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => openAccountDetail(acc)} data-testid={`account-row-${acc.account_id}`}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-slate-900">{acc.business_name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{acc.account_id}</div>
                      </td>
                      <td className="px-4 py-3"><div className="text-xs text-slate-600">{acc.email || "-"}</div><div className="text-[10px] text-slate-400">{acc.phone || ""}</div></td>
                      <td className="text-center px-4 py-3"><Badge className={`text-[10px] ${planColors[acc.plan] || "bg-slate-100"}`}>{acc.plan}</Badge></td>
                      <td className="text-center px-4 py-3"><Badge className={`text-[10px] ${statusColors[acc.status] || "bg-slate-100"}`}>{acc.status}</Badge></td>
                      <td className="text-center text-xs text-slate-600 px-4 py-3">{acc.user_count || 0}</td>
                      <td className="text-center text-xs text-slate-400 px-4 py-3">{acc.created_at ? new Date(acc.created_at).toLocaleDateString() : "-"}</td>
                      <td className="text-right px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditAccount(acc)} data-testid={`edit-account-${acc.account_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleStatus(acc)} className={acc.status === "active" ? "text-red-500 hover:text-red-700" : "text-emerald-500 hover:text-emerald-700"} data-testid={`toggle-account-${acc.account_id}`}><Power className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => openAccountDetail(acc)} className="text-slate-400"><ChevronRight className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* === SERVICE CATALOG TAB === */}
        {activeTab === "catalog" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Service Catalog</h2>
                <p className="text-xs text-slate-500">Define provisionable items and their monthly/one-time costs</p>
              </div>
              <Button onClick={openCreateCatalog} className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8" data-testid="create-catalog-item"><Plus className="h-3.5 w-3.5 mr-1" /> New Item</Button>
            </div>
            {catalog.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm mb-3">No catalog items yet. Add your first provisionable service.</p>
                <Button onClick={openCreateCatalog} className="bg-slate-900 text-white text-xs"><Plus className="h-3.5 w-3.5 mr-1" /> Add Item</Button>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
                {catalog.map((item) => {
                  const Icon = categoryIcon(item.category);
                  return (
                    <div key={item.catalog_item_id} className={`bg-white border rounded-lg p-4 transition-shadow hover:shadow-md ${!item.is_active ? "opacity-50" : ""}`} data-testid={`catalog-item-${item.catalog_item_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="h-4.5 w-4.5 text-slate-600" /></div>
                          <div>
                            <h3 className="text-sm font-semibold text-slate-900">{item.name}</h3>
                            <Badge variant="outline" className="text-[10px] mt-0.5">{item.category}</Badge>
                          </div>
                        </div>
                        {!item.is_active && <Badge className="bg-slate-200 text-slate-500 text-[10px]">Inactive</Badge>}
                      </div>
                      {item.description && <p className="text-xs text-slate-500 mb-3">{item.description}</p>}
                      <div className="flex items-center gap-4 mb-3">
                        <div>
                          <span className="text-[10px] text-slate-400 uppercase tracking-wide">Monthly</span>
                          <p className={`text-lg font-bold ${item.monthly_cost > 0 ? "text-slate-900" : "text-slate-300"}`}>${item.monthly_cost.toFixed(2)}</p>
                        </div>
                        {item.one_time_cost > 0 && (
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase tracking-wide">One-time</span>
                            <p className="text-lg font-bold text-slate-600">${item.one_time_cost.toFixed(2)}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 border-t border-slate-100 pt-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditCatalog(item)} className="text-xs text-slate-500"><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteCatalogItem(item.catalog_item_id)} className="text-xs text-red-500 hover:text-red-700"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* === ACCOUNT DETAIL TAB === */}
        {activeTab === "account-detail" && selectedAccount && (
          <>
            <button onClick={() => { setActiveTab("accounts"); setSelectedAccount(null); }} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4" data-testid="back-to-accounts">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Accounts
            </button>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{selectedAccount.business_name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-slate-400 font-mono">{selectedAccount.account_id}</span>
                  <Badge className={`text-[10px] ${planColors[selectedAccount.plan] || ""}`}>{selectedAccount.plan}</Badge>
                  <Badge className={`text-[10px] ${statusColors[selectedAccount.status] || ""}`}>{selectedAccount.status}</Badge>
                </div>
                {selectedAccount.email && <p className="text-xs text-slate-500 mt-1">{selectedAccount.email} {selectedAccount.phone && `| ${selectedAccount.phone}`}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => openEditAccount(selectedAccount)} className="text-xs"><Pencil className="h-3 w-3 mr-1" /> Edit</Button>
                <Button onClick={openAddProvision} className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8" data-testid="add-provision-btn" disabled={catalog.length === 0}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Provision Item
                </Button>
              </div>
            </div>

            {/* Billing Summary */}
            {billing && (
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 mb-6 text-white" data-testid="billing-summary">
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-4 w-4 text-white/70" />
                  <h3 className="text-sm font-semibold">Billing Summary</h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">Monthly Recurring</p>
                    <p className="text-2xl font-bold">${billing.total_monthly.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">One-time Charges</p>
                    <p className="text-2xl font-bold">${billing.total_one_time.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">Active Items</p>
                    <p className="text-2xl font-bold">{provisions.filter(p => p.is_active).length}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Provisioned Items */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="provisions-table">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-sm font-semibold text-slate-700">Provisioned Services & Equipment</h3>
              </div>
              {provisions.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">
                  No items provisioned yet. Click "Provision Item" to add services.
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2">Item</th>
                      <th className="text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2">Category</th>
                      <th className="text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2">Qty</th>
                      <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2">Unit Cost</th>
                      <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2">Monthly</th>
                      <th className="text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2">Status</th>
                      <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {provisions.map((p) => {
                      const Icon = categoryIcon(p.item_category);
                      const unitCost = p.cost_override != null ? p.cost_override : p.catalog_monthly_cost;
                      return (
                        <tr key={p.provision_id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${!p.is_active ? "opacity-50" : ""}`} data-testid={`provision-row-${p.provision_id}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-slate-400" />
                              <div>
                                <span className="text-sm font-medium text-slate-900">{p.item_name}</span>
                                {p.notes && <p className="text-[10px] text-slate-400">{p.notes}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="text-center px-4 py-2.5"><Badge variant="outline" className="text-[10px]">{p.item_category}</Badge></td>
                          <td className="text-center text-sm font-medium text-slate-700 px-4 py-2.5">{p.quantity}</td>
                          <td className="text-right px-4 py-2.5">
                            <span className="text-sm text-slate-700">${unitCost.toFixed(2)}</span>
                            {p.cost_override != null && <span className="text-[10px] text-amber-600 ml-1">(override)</span>}
                          </td>
                          <td className="text-right text-sm font-semibold text-slate-900 px-4 py-2.5">${p.effective_monthly_cost.toFixed(2)}</td>
                          <td className="text-center px-4 py-2.5">
                            <Badge className={p.is_active ? "bg-emerald-100 text-emerald-700 text-[10px]" : "bg-slate-100 text-slate-500 text-[10px]"}>{p.is_active ? "Active" : "Inactive"}</Badge>
                          </td>
                          <td className="text-right px-4 py-2.5">
                            <div className="flex items-center justify-end gap-0.5">
                              <Button variant="ghost" size="sm" onClick={() => openEditProvision(p)} className="h-7 w-7 p-0"><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteProvision(p.provision_id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {billing && (
                    <tfoot>
                      <tr className="bg-slate-50 border-t border-slate-200">
                        <td colSpan={4} className="text-right text-xs font-semibold text-slate-600 px-4 py-2.5">Total Monthly</td>
                        <td className="text-right text-sm font-bold text-slate-900 px-4 py-2.5">${billing.total_monthly.toFixed(2)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Account Create/Edit Dialog */}
      <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle>{editingAccount ? "Edit Account" : "New Account"}</DialogTitle>
            <DialogDescription>Manage business account details and plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Business Name *</Label><Input value={accountForm.business_name} onChange={(e) => setAccountForm({ ...accountForm, business_name: e.target.value })} className="mt-1" data-testid="account-business-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={accountForm.email} onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} className="mt-1" data-testid="account-email" /></div>
              <div><Label>Phone</Label><Input value={accountForm.phone} onChange={(e) => setAccountForm({ ...accountForm, phone: e.target.value })} className="mt-1" data-testid="account-phone" /></div>
            </div>
            <div><Label>Address</Label><Input value={accountForm.address} onChange={(e) => setAccountForm({ ...accountForm, address: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>City</Label><Input value={accountForm.city} onChange={(e) => setAccountForm({ ...accountForm, city: e.target.value })} className="mt-1" /></div>
              <div><Label>State</Label><Input value={accountForm.state} onChange={(e) => setAccountForm({ ...accountForm, state: e.target.value })} className="mt-1" /></div>
              <div><Label>Zip</Label><Input value={accountForm.zip_code} onChange={(e) => setAccountForm({ ...accountForm, zip_code: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Plan</Label><Select value={accountForm.plan} onValueChange={(v) => setAccountForm({ ...accountForm, plan: v })}><SelectTrigger className="mt-1" data-testid="account-plan"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="trial">Trial</SelectItem><SelectItem value="basic">Basic</SelectItem><SelectItem value="pro">Pro</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent></Select></div>
              <div><Label>Status</Label><Select value={accountForm.status} onValueChange={(v) => setAccountForm({ ...accountForm, status: v })}><SelectTrigger className="mt-1" data-testid="account-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountDialog(false)}>Cancel</Button>
            <Button onClick={saveAccount} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="account-save-btn">{editingAccount ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Catalog Item Dialog */}
      <Dialog open={showCatalogDialog} onOpenChange={setShowCatalogDialog}>
        <DialogContent className="sm:max-w-md" data-testid="catalog-dialog">
          <DialogHeader>
            <DialogTitle>{editingCatalogItem ? "Edit Catalog Item" : "New Catalog Item"}</DialogTitle>
            <DialogDescription>Define a provisionable service or equipment with pricing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name *</Label><Input value={catalogForm.name} onChange={(e) => setCatalogForm({ ...catalogForm, name: e.target.value })} placeholder="e.g., POS Station, Receipt Printer" className="mt-1" data-testid="catalog-name" /></div>
            <div><Label>Category</Label>
              <Select value={catalogForm.category} onValueChange={(v) => setCatalogForm({ ...catalogForm, category: v })}>
                <SelectTrigger className="mt-1" data-testid="catalog-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Input value={catalogForm.description} onChange={(e) => setCatalogForm({ ...catalogForm, description: e.target.value })} placeholder="Optional description" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Monthly Cost ($)</Label><Input type="number" step="0.01" value={catalogForm.monthly_cost} onChange={(e) => setCatalogForm({ ...catalogForm, monthly_cost: e.target.value })} className="mt-1" data-testid="catalog-monthly-cost" /></div>
              <div><Label>One-time Cost ($)</Label><Input type="number" step="0.01" value={catalogForm.one_time_cost} onChange={(e) => setCatalogForm({ ...catalogForm, one_time_cost: e.target.value })} className="mt-1" data-testid="catalog-onetime-cost" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={catalogForm.is_active} onCheckedChange={(v) => setCatalogForm({ ...catalogForm, is_active: v })} data-testid="catalog-active-toggle" />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatalogDialog(false)}>Cancel</Button>
            <Button onClick={saveCatalogItem} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="catalog-save-btn">{editingCatalogItem ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provision Dialog */}
      <Dialog open={showProvisionDialog} onOpenChange={setShowProvisionDialog}>
        <DialogContent className="sm:max-w-md" data-testid="provision-dialog">
          <DialogHeader>
            <DialogTitle>{editingProvision ? "Edit Provision" : "Provision Item"}</DialogTitle>
            <DialogDescription>Assign a catalog item to {selectedAccount?.business_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Service/Equipment *</Label>
              <Select value={provisionForm.catalog_item_id} onValueChange={(v) => setProvisionForm({ ...provisionForm, catalog_item_id: v })}>
                <SelectTrigger className="mt-1" data-testid="provision-item-select"><SelectValue placeholder="Select item..." /></SelectTrigger>
                <SelectContent>
                  {catalog.filter(c => c.is_active).map((c) => (
                    <SelectItem key={c.catalog_item_id} value={c.catalog_item_id}>
                      {c.name} — ${c.monthly_cost.toFixed(2)}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantity</Label><Input type="number" min="1" value={provisionForm.quantity} onChange={(e) => setProvisionForm({ ...provisionForm, quantity: e.target.value })} className="mt-1" data-testid="provision-qty" /></div>
              <div><Label>Cost Override ($/mo)</Label><Input type="number" step="0.01" value={provisionForm.cost_override} onChange={(e) => setProvisionForm({ ...provisionForm, cost_override: e.target.value })} placeholder="Use catalog price" className="mt-1" data-testid="provision-cost-override" /></div>
            </div>
            <div><Label>Notes</Label><Input value={provisionForm.notes} onChange={(e) => setProvisionForm({ ...provisionForm, notes: e.target.value })} placeholder="e.g., Serial #, location" className="mt-1" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={provisionForm.is_active} onCheckedChange={(v) => setProvisionForm({ ...provisionForm, is_active: v })} data-testid="provision-active-toggle" />
              <Label>Active</Label>
            </div>
            {/* Preview cost */}
            {provisionForm.catalog_item_id && (() => {
              const catItem = catalog.find(c => c.catalog_item_id === provisionForm.catalog_item_id);
              if (!catItem) return null;
              const unitCost = provisionForm.cost_override !== "" ? parseFloat(provisionForm.cost_override) : catItem.monthly_cost;
              const qty = parseInt(provisionForm.quantity) || 1;
              return (
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Estimated Monthly</p>
                  <p className="text-xl font-bold text-slate-900">${(unitCost * qty).toFixed(2)}<span className="text-xs font-normal text-slate-400">/mo</span></p>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProvisionDialog(false)}>Cancel</Button>
            <Button onClick={saveProvision} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="provision-save-btn">{editingProvision ? "Update" : "Provision"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
